import React, { useState, useEffect, useRef } from "react";

const DefaultValueDropdown = ({ value, onChange, options = [], multiple = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const instanceId = useRef(`default-dropdown-${Math.random()}`);

  // Listen for other dropdown opening events
  useEffect(() => {
    const handleOtherDropdownOpen = (event) => {
      if (event.detail.instanceId !== instanceId.current) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    window.addEventListener("dropdownOpened", handleOtherDropdownOpen);
    return () => {
      window.removeEventListener("dropdownOpened", handleOtherDropdownOpen);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleDropdownClick = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      // Broadcast that this dropdown is opening
      window.dispatchEvent(
        new CustomEvent("dropdownOpened", {
          detail: { instanceId: instanceId.current },
        })
      );

      // Focus input after a short delay to ensure dropdown is rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      setSearchTerm("");
    }
  };

  const handleOptionSelect = (optionValue) => {
    if (multiple) {
      // Multi-select: toggle selection
      const currentValues = Array.isArray(value) ? value : (value ? value.split(';') : []);
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues.join(';'));
      // Don't close dropdown for multi-select
    } else {
      // Single-select: select and close
      onChange(optionValue);
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const getDisplayText = () => {
    if (multiple) {
      // Multi-select display
      const values = Array.isArray(value) ? value : (value ? value.split(';') : []);
      const selectedValues = values.filter(v => v);
      const selectedCount = selectedValues.length;

      if (selectedCount === 0) return "-- Select Values --";
      if (selectedCount === 1) {
        // Show label when only 1 value selected
        const option = options.find((opt) => opt.value === selectedValues[0]);
        return option ? option.label : selectedValues[0];
      }
      return `${selectedCount} selected values`;
    } else {
      // Single-select display
      if (!value) return "-- Select Value --";
      const option = options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }
  };

  const isSelected = (optionValue) => {
    if (multiple) {
      const values = Array.isArray(value) ? value : (value ? value.split(';') : []);
      return values.includes(optionValue);
    }
    return value === optionValue;
  };

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <div className="dropdown-header" onClick={handleDropdownClick}>
        <span className="dropdown-text">{getDisplayText()}</span>
        <svg
          className={`dropdown-arrow ${isOpen ? "open" : ""}`}
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#706E6B"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-search">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 13L10.1 10.1M11.6667 6.33333C11.6667 9.27885 9.27885 11.6667 6.33333 11.6667C3.38781 11.6667 1 9.27885 1 6.33333C1 3.38781 3.38781 1 6.33333 1C9.27885 1 11.6667 3.38781 11.6667 6.33333Z"
                stroke="#706E6B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="dropdown-search-input"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="dropdown-options">
            {/* None option - only for single select */}
            {!multiple && (
              <div
                className={`dropdown-option ${!value ? "selected" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect("");
                }}
              >
                <span className="option-label">-- None --</span>
              </div>
            )}

            {/* Filtered options */}
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`dropdown-option ${
                  isSelected(option.value) ? "selected" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionSelect(option.value);
                }}
              >
                {multiple && (
                  <input
                    type="checkbox"
                    checked={isSelected(option.value)}
                    onChange={() => {}}
                    style={{ marginRight: '8px', pointerEvents: 'none' }}
                  />
                )}
                <span className="option-label">{option.label}</span>
              </div>
            ))}

            {filteredOptions.length === 0 && searchTerm && (
              <div className="dropdown-option disabled">
                <span className="option-label">No options found</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultValueDropdown;
